import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as userApi from '@/api/user'
import request from '@/api/index'
import type { UserInfo, LoginForm, BindForm } from '@/types/user'

export const useUserStore = defineStore('user', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const userInfo = ref<UserInfo | null>(null)
  const userCities = ref<{ code: string; name: string }[]>([])
  const currentCityCode = ref<string>(localStorage.getItem('current_city') || 'sh')
  const loading = ref(false)
  
  const isLoggedIn = computed(() => !!token.value)
  const userName = computed(() => userInfo.value?.name || '')
  const userId = computed(() => userInfo.value?.id)
  
  const loginByPassword = async (form: LoginForm) => {
    loading.value = true
    try {
      const res = await userApi.loginByPassword(form)
      if (res.data.result === 1) {
        token.value = res.data.data.token
        userInfo.value = res.data.data.user
        localStorage.setItem('token', token.value!)
        await fetchUserInfo() // 登录成功后立刻拉取城市等信息
        return res.data.data
      } else {
        throw new Error(res.data.msg)
      }
    } finally {
      loading.value = false
    }
  }
  
  const loginByWechat = async (key: string) => {
    const res = await userApi.loginByWechat(key)
    if (res.data.result === 1) {
      token.value = res.data.data.token
      userInfo.value = res.data.data.user
      localStorage.setItem('token', token.value!)
      await fetchUserInfo() // 登录成功后立刻拉取城市等信息
      return res.data.data
    } else {
      throw new Error(res.data.msg)
    }
  }
  
  const bindMobile = async (form: BindForm) => {
    const res = await userApi.bindMobile(form)
    if (res.data.result === 1) {
      token.value = res.data.data.token
      userInfo.value = res.data.data.user
      localStorage.setItem('token', token.value!)
      return res.data.data
    } else {
      throw new Error(res.data.msg)
    }
  }
  
  const fetchUserInfo = async () => {
    if (!token.value) return
    try {
      const [infoRes, citiesRes] = await Promise.all([
        userApi.getUserInfo(),
        request.get('/user/cities')
      ])
      
      if (infoRes.data.result === 1) {
        userInfo.value = infoRes.data.data
      }
      
      if (citiesRes.data && citiesRes.data.result === 1) {
        userCities.value = citiesRes.data.data
        
        // 如果当前选中的城市不在用户的城市列表中，则默认选中列表中的第一个城市
        if (userCities.value.length > 0) {
          const currentCityCodeStr = currentCityCode.value;
          if (!currentCityCodeStr || !userCities.value.some(c => c.code === currentCityCodeStr)) {
            setCurrentCity(userCities.value[0].code)
          }
        }
      }
    } catch {
      logout()
    }
  }

  const setCurrentCity = (cityCode: string) => {
    currentCityCode.value = cityCode
    localStorage.setItem('current_city', cityCode)
  }
  
  const logout = async () => {
    if (token.value) {
      await userApi.logout().catch(() => undefined)
    }
    token.value = null
    userInfo.value = null
    userCities.value = []
    currentCityCode.value = ''
    localStorage.removeItem('token')
    localStorage.removeItem('current_city')
  }
  
  return {
    token,
    userInfo,
    userCities,
    currentCityCode,
    loading,
    isLoggedIn,
    userName,
    userId,
    loginByPassword,
    loginByWechat,
    bindMobile,
    fetchUserInfo,
    logout,
    setCurrentCity,
  }
})
